import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import SnackBar from 'modules/common/components/SnackBar';
import Modal from 'modules/common/components/Modal';
import AdvancedFiltersModal from '../components/AdvancedFiltersModal/AdvancedFiltersModal';
import CreateBundleModal from '../components/CreateBundleModal/CreateBundleModal';
import CreateDiscountModal from '../components/CreateDiscountModal/CreateDiscountModal';
import DiscountCardGrid from '../components/DiscountCardGrid/DiscountCardGrid';
import DiscountFilters from '../components/DiscountFilters/DiscountFilters';
import EditOfferModal from '../components/EditOfferModal/EditOfferModal';
import {
  updateDiscount as updateDiscountAction,
  performOfferAction as performOfferActionAction,
} from '../actions';
import { DiscountActions } from '../constants';
import { useOffersList } from '../hooks/useOffersList';
import discountsStrings from '../strings';
import type { DiscountActionType, DiscountItem } from '../types/discount.types';
import { extractApiErrorMessage } from '../utils/discountHelpers';
import { DiscountsPageWrapper, ConfirmModalMessage } from './Discounts.style';

type SnackBarType = 'success' | 'error';

interface PerformOfferActionPayload {
  offerKey: string | number;
  actionType: DiscountActionType;
  discountType?: string;
  discountValue?: number;
  startDate?: string;
  endDate?: string;
}

interface DiscountsContainerStateProps {
  isCreatingDiscount: boolean;
  createDiscountSuccess: boolean;
  isUpdatingDiscount: boolean;
  updateDiscountSuccess: boolean;
  isPerformingAction: boolean;
  performActionSuccess: boolean;
  performActionError: unknown;
  createDiscountError: unknown;
}

interface DiscountsContainerDispatchProps {
  updateDiscount: (payload?: unknown) => void;
  performOfferAction: (payload: PerformOfferActionPayload) => void;
}

type DiscountsContainerProps = DiscountsContainerStateProps & DiscountsContainerDispatchProps;

interface DiscountsRootState {
  pharmacyDiscounts: DiscountsContainerStateProps;
}

interface SnackBarState {
  show: boolean;
  message: string;
  type: SnackBarType | null;
}

const DiscountsContainer = ({
  isCreatingDiscount,
  createDiscountSuccess,
  isUpdatingDiscount,
  updateDiscountSuccess,
  isPerformingAction,
  performActionSuccess,
  performActionError,
  createDiscountError,
  updateDiscount: dispatchUpdateDiscount,
  performOfferAction: dispatchPerformOfferAction,
}: DiscountsContainerProps) => {
  const {
    stockFilter,
    statusFilter,
    itemTypeFilter,
    advancedFilters,
    filters,
    setStockFilter,
    setStatusFilter,
    setItemTypeFilter,
    setAdvancedFilters,
    discounts,
    isLoading,
    isLoadingMore,
    isError,
    hasMore,
    loadMore,
    reload,
  } = useOffersList();
  const [isCreateDiscountOpen, setIsCreateDiscountOpen] = useState(false);
  const [isCreateBundleOpen, setIsCreateBundleOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<DiscountItem | null>(null);
  const [editingActionType, setEditingActionType] =
    useState<DiscountActionType>(DiscountActions.Edit);
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [confirmingStopAction, setConfirmingStopAction] = useState<{
    discount: DiscountItem;
    actionType: DiscountActionType;
  } | null>(null);
  const [snackBar, setSnackBar] = useState<SnackBarState>({ show: false, message: '', type: null });

  const showSnackBar = (message: string, type: SnackBarType | null = null) => {
    setSnackBar({ show: true, message, type });
    setTimeout(() => setSnackBar({ show: false, message: '', type: null }), 3000);
  };

  useEffect(() => {
    if (createDiscountSuccess) {
      showSnackBar(discountsStrings.createSuccess, 'success');
      setIsCreateDiscountOpen(false);
      setIsCreateBundleOpen(false);
      reload();
    }
  }, [createDiscountSuccess, reload]);

  useEffect(() => {
    if (updateDiscountSuccess) {
      showSnackBar(discountsStrings.updateSuccess, 'success');
    }
  }, [updateDiscountSuccess]);

  useEffect(() => {
    if (performActionSuccess) {
      showSnackBar(discountsStrings.updateSuccess, 'success');
      setEditingDiscount(null);
      setEditingActionType(DiscountActions.Edit);
      setConfirmingStopAction(null);
      reload();
    }
  }, [performActionSuccess, reload]);

  useEffect(() => {
    if (!performActionError) return;
    showSnackBar(extractApiErrorMessage(performActionError), 'error');
  }, [performActionError]);

  useEffect(() => {
    if (!createDiscountError) return;
    showSnackBar(extractApiErrorMessage(createDiscountError), 'error');
  }, [createDiscountError]);

  const openCreateFlow = () => {
    setIsCreateBundleOpen(false);
    setIsCreateDiscountOpen(true);
  };

  const openCreateBundleFlow = () => {
    setIsCreateDiscountOpen(false);
    setIsCreateBundleOpen(true);
  };

  const handleCloseCreateFlow = () => {
    setIsCreateDiscountOpen(false);
  };

  const handleCloseCreateBundleFlow = () => {
    setIsCreateBundleOpen(false);
  };

  const handleDiscountAction = (discount: DiscountItem, actionType: string) => {
    if (actionType === DiscountActions.Edit || actionType === DiscountActions.Republish) {
      setEditingDiscount(discount);
      setEditingActionType(actionType);
      return;
    }

    if (actionType === DiscountActions.Stop) {
      setConfirmingStopAction({ discount, actionType });
    }
  };

  const handleConfirmStopAction = () => {
    if (!confirmingStopAction) return;
    dispatchPerformOfferAction({
      offerKey: confirmingStopAction.discount.id,
      actionType: confirmingStopAction.actionType,
    });
    setConfirmingStopAction(null);
  };

  const handleCancelStopAction = () => {
    setConfirmingStopAction(null);
  };

  const handleCloseEditOffer = () => {
    setEditingDiscount(null);
    setEditingActionType(DiscountActions.Edit);
  };

  return (
    <DiscountsPageWrapper>
      <DiscountFilters
        statusFilter={statusFilter}
        stockFilter={stockFilter}
        itemTypeFilter={itemTypeFilter}
        onStatusChange={setStatusFilter}
        onStockChange={setStockFilter}
        onItemTypeChange={setItemTypeFilter}
        onCreateOffer={openCreateFlow}
        onCreateBundle={openCreateBundleFlow}
        onOpenAdvancedFilters={() => setIsAdvancedFiltersOpen(true)}
      />

      <AdvancedFiltersModal
        isOpen={isAdvancedFiltersOpen}
        filters={advancedFilters}
        onClose={() => setIsAdvancedFiltersOpen(false)}
        onApply={setAdvancedFilters}
      />

      <DiscountCardGrid
        discounts={discounts}
        isLoading={isLoading}
        isError={isError}
        onAction={handleDiscountAction}
        onCreateOffer={openCreateFlow}
        onCreateBundle={openCreateBundleFlow}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
      />

      <CreateDiscountModal
        key={isCreateDiscountOpen ? 'create-discount-open' : 'create-discount-closed'}
        isOpen={isCreateDiscountOpen}
        isSubmitting={isCreatingDiscount}
        onClose={handleCloseCreateFlow}
      />

      <CreateBundleModal
        key={isCreateBundleOpen ? 'create-bundle-open' : 'create-bundle-closed'}
        isOpen={isCreateBundleOpen}
        isSubmitting={isCreatingDiscount}
        onClose={handleCloseCreateBundleFlow}
      />

      <EditOfferModal
        key={editingDiscount ? `edit-${editingDiscount.id}` : 'edit-closed'}
        isOpen={Boolean(editingDiscount)}
        discount={editingDiscount}
        onClose={handleCloseEditOffer}
        actionType={editingActionType}
        isSaving={isPerformingAction}
        title={
          editingActionType === DiscountActions.Republish
            ? discountsStrings.actionRepublishOffer
            : discountsStrings.editOfferTitle
        }
      />

      <Modal
        key={confirmingStopAction ? `stop-${confirmingStopAction.discount.id}` : 'stop-closed'}
        toggle={Boolean(confirmingStopAction)}
        title={discountsStrings.confirmStopOfferTitle}
        onClose={handleCancelStopAction}
        onCancelHandler={handleCancelStopAction}
        cancelActionLabel={discountsStrings.cancelBtn}
        onConfirmHandler={handleConfirmStopAction}
        confirmActionLabel={discountsStrings.confirmStopOfferTitle}
        confirmActionLoading={isPerformingAction}
        extendConfirmButtonStyle="background: #e53e3e; color: #fff; border: none;"
        closeOnClickOutside={false}
        size="sm"
      >
        <ConfirmModalMessage>{discountsStrings.confirmStopOfferMessage}</ConfirmModalMessage>
      </Modal>

      <SnackBar
        show={snackBar.show}
        message={snackBar.message}
        type={snackBar.type ?? undefined}
      />
    </DiscountsPageWrapper>
  );
};

const mapStateToProps = (state: DiscountsRootState): DiscountsContainerStateProps => ({
  isCreatingDiscount: state.pharmacyDiscounts.isCreatingDiscount,
  createDiscountSuccess: state.pharmacyDiscounts.createDiscountSuccess,
  isUpdatingDiscount: state.pharmacyDiscounts.isUpdatingDiscount,
  updateDiscountSuccess: state.pharmacyDiscounts.updateDiscountSuccess,
  isPerformingAction: state.pharmacyDiscounts.isPerformingAction,
  performActionSuccess: state.pharmacyDiscounts.performActionSuccess,
  performActionError: state.pharmacyDiscounts.performActionError,
  createDiscountError: state.pharmacyDiscounts.createDiscountError,
});

const mapDispatchToProps = (dispatch: Dispatch): DiscountsContainerDispatchProps =>
  bindActionCreators(
    {
      updateDiscount: updateDiscountAction,
      performOfferAction: performOfferActionAction,
    },
    dispatch,
  );

export default connect<
  DiscountsContainerStateProps,
  DiscountsContainerDispatchProps,
  {},
  DiscountsRootState
>(mapStateToProps, mapDispatchToProps)(DiscountsContainer);
